import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';
import { InventoryItemDto } from '../../api/inventoryItemDto';
import { NarwhalQueryParameters } from '../../api/narwhal.query';
import { NarwhalService } from '../../narwhal.service';
import { defs } from '../../defs';
import { Generation, Sex } from 'src/app/shared/filter/filter';

const sortByTranslationKeys = [
	'sortByOptions.like',
	'sortByOptions.productivity',
	'sortByOptions.age',
	'sortByOptions.fitness',
	'sortByOptions.priceInGtr'
];

@Component({
	selector: 'narwhalia-my-swarm-sidebar',
	templateUrl: './my-swarm-sidebar.component.html',
	styleUrls: ['./my-swarm-sidebar.component.scss']
})
export class MySwarmSidebarComponent implements OnInit {
	populationFilter: FormGroup;
	sortByParams: any[];
	tmpClanName: string;
	totalCount: number;
	inventoryItems: InventoryItemDto[];
	showInventoryPopup: boolean;
	inventorySum: number;

	constructor(
		private fb: FormBuilder,
		private router: Router,
		private route: ActivatedRoute,
		private narwhalService: NarwhalService,
		private translateService: TranslateService
	) {
		this.translateService.get(sortByTranslationKeys).subscribe(sortByOptions => {
			const [like, productivity, age, fitness, priceInGtr] = sortByTranslationKeys;
			this.sortByParams = [
				{ viewValue: sortByOptions[like], value: 'like' },
				{ viewValue: sortByOptions[productivity], value: 'productivity' },
				{ viewValue: sortByOptions[age], value: 'age' },
				{ viewValue: sortByOptions[fitness], value: 'fitness' },
				{ viewValue: sortByOptions[priceInGtr], value: 'price' }
			];
		});

		this.createForm();
		this.narwhalService.totalCount$.subscribe(totalCount => (this.totalCount = totalCount));
		this.narwhalService.inventoryItems$.subscribe(inventoryItems => {
			this.inventorySum = inventoryItems.reduce((acc, curr) => acc + curr.count, 0);
		});
		this.route.queryParams.subscribe((params: NarwhalQueryParameters) => {
			const { clanName } = params;
			this.tmpClanName = clanName;
			this.populationFilter.get('clanName').setValue(clanName);
		});
	}

	toggleInventoryPopup(event) {
		event.stopPropagation();
		this.showInventoryPopup = !this.showInventoryPopup;
	}

	createForm() {
		this.populationFilter = this.fb.group({
			sortBy: [defs.sortOrders.defaults.mySwarm],
			sortOrder: [''],
			clanName: [''],
			generation: [''],
			forSale: [undefined],
			exclusive: [undefined],
			male: [false],
			female: [false],
			firstGen: [false],
			secondGen: [false]
		});
	}

	patchForm = queryParams => {
		const { forSale, clanName, exclusive, gender, gen, sortBy } = queryParams;
		this.populationFilter.patchValue({
			forSale: forSale === 'true',
			clanName,
			exclusive: exclusive === 'true',
			male: Sex.isMale(gender),
			female: Sex.isFemale(gender),
			firstGen: Generation.firstGen(gen),
			secondGen: Generation.secondGen(gen),
			sortBy
		});
	};

	refreshUrlQueryParams = ({ forSale, exclusive, male, female, firstGen, secondGen, sortBy }) => {

		let queryParams: NarwhalQueryParameters = {
			gender: Sex.from(male, female),
			gen: Generation.from(firstGen, secondGen),
			sortBy
		};

		if (forSale) {
			queryParams = { ...queryParams, forSale };
		}

		if (exclusive) {
			queryParams = { ...queryParams, exclusive };
		}

		if (this.tmpClanName) {
			queryParams = { ...queryParams, clanName: this.tmpClanName };
		}

		this.router.navigate(['my-swarm'], { queryParams });
	};

	submitClanName() {
		this.tmpClanName = this.populationFilter.get('clanName').value;
		this.refreshUrlQueryParams({ ...this.populationFilter.value });
	}

	handleGenderChange() {
		this.checkContrary('male', 'female');
		this.checkContrary('female', 'male');
	}

	handleGenerationChange() {
		this.checkContrary('firstGen', 'secondGen');
		this.checkContrary('secondGen', 'firstGen');
	}

	checkContrary(firstFormControl, secondFormControl) {
		this.populationFilter
			.get(firstFormControl)
			.valueChanges.subscribe(
				secondGenValue =>
					secondGenValue &&
					this.populationFilter.get(secondFormControl).value &&
					this.populationFilter.get(secondFormControl).setValue(false)
			);
	}

	ngOnInit() {
		this.narwhalService.mySwarmQueryParams$.pipe(first(queryParams => Object.keys(queryParams).length > 0)).subscribe(queryParams => {
			// init the form
			this.patchForm(queryParams);
			this.tmpClanName = this.populationFilter.get('clanName').value;

			// subscribe to changes after the patch is done
			this.populationFilter.valueChanges.subscribe(this.refreshUrlQueryParams);
			this.handleGenderChange();
			this.handleGenerationChange();
		});
	}
}